import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { formatCreatedOn, formatPrice } from "../../brand/planFormat";
import {
  PAYMENT_STATUS_PILL,
  PAYMENT_STATUS_STYLES,
} from "../paymentStatusStyles";
import type { RevenuePayment } from "../types/revenue.types";

const COLUMNS = [
  "Sl",
  "Client name",
  "Place",
  "Goal",
  "Amount",
  "Duration",
  "Generated on",
  "Paid on",
  "Status",
];

const headCell =
  "px-3 sm:px-4 py-3 text-[13px] font-normal text-ink-muted bg-white shadow-[inset_0_-1px_0_#E9EDF1]";
const bodyCell = "px-3 sm:px-4 py-4 text-[14px] text-ink";

const formatDate = (iso: string): string => formatCreatedOn(new Date(iso));

interface RevenueTableProps {
  payments: RevenuePayment[];
  /** The number the first visible row is counted from, so the "Sl" column keeps
   *  counting across pages instead of restarting at one on each. */
  startIndex: number;
  /** Stands in for the table body when the list comes back with no rows. */
  empty: EmptyStateProps;
  /**
   * Where the list is. While it is on its way the body says nothing — no rows
   * and no message — and only a load slow enough to notice shimmers, as one
   * block the size of the table.
   */
  phase?: LoadPhase;
}

/**
 * Every payment the workspace has raised, one row each.
 *
 * The table reports rather than acts: nothing here is clickable, because a
 * payment is not a screen of its own and settling one is done where it was
 * raised — a client's Payments tab, or the enquiry it came from.
 */
const RevenueTable: React.FC<RevenueTableProps> = ({
  payments,
  startIndex,
  empty,
  phase = "ready",
}) =>
  phase !== "ready" && payments.length === 0 ? (
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
  // The only scroll region on the page: rows move, the sticky header row and
  // the card's pagination footer stay put.
  <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
    {/* Full height only when the rows are gone — see `ClientsTable`. */}
    <table
      className={`w-full table-fixed border-collapse text-left ${
        // The width the columns need is only worth insisting on when there
        // are rows to lay out in them. With none, the min-width would push
        // the empty state out past a phone’s edge and leave the user
        // scrolling sideways to read why the list is empty — so the table
        // takes the scroll region as it finds it and the state centres in it.
        payments.length === 0 ? "h-full" : "min-w-[980px]"
      }`}
    >
      <colgroup>
        <col className="w-[5%]" />
        <col className="w-[17%]" />
        <col className="w-[12%]" />
        <col className="w-[13%]" />
        <col className="w-[9%]" />
        <col className="w-[10%]" />
        <col className="w-[12%]" />
        <col className="w-[12%]" />
        <col className="w-[10%]" />
      </colgroup>
      <thead className="sticky top-0 z-10">
        <tr>
          {COLUMNS.map((heading) => (
            <th key={heading} scope="col" className={headCell}>
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      {payments.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={COLUMNS.length} className="align-middle">
              <EmptyState {...empty} />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {payments.map((payment, index) => (
            <tr
              key={payment.id}
              className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors"
            >
              <td className={bodyCell}>
                {String(startIndex + index + 1).padStart(2, "0")}
              </td>
              <td className={`${bodyCell} font-medium truncate`}>
                {payment.clientName}
              </td>
              <td className={`${bodyCell} truncate`}>{payment.place}</td>
              <td className={`${bodyCell} truncate`}>{payment.goal}</td>
              <td className={bodyCell}>{formatPrice(payment.amount)}</td>
              <td className={bodyCell}>{payment.duration}</td>
              <td className={bodyCell}>{formatDate(payment.generatedAt)}</td>
              {/* Anything not paid has no date to show, and leaving the cell
                  blank would read as one lost. */}
              <td className={bodyCell}>
                {payment.paidAt ? formatDate(payment.paidAt) : "NA"}
              </td>
              <td className={bodyCell}>
                <span
                  className={`${PAYMENT_STATUS_PILL} ${
                    PAYMENT_STATUS_STYLES[payment.status]
                  }`}
                >
                  {payment.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      )}
    </table>
  </div>
  );

export default RevenueTable;
